<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Alert, Select, Stack, Text } from '@Shelly/shelly-design-system';
	import {
		RUN_SCRIPT_SCENE_SET_VALUES,
		getCachedDeviceScripts,
		getDeviceRealId,
		loadDeviceScripts,
		mapScriptsToSelectOptions,
		type RunScriptCommand,
		type ScriptSelectOption,
	} from 'src/helpers/device_script_action_helpers';
	import { is_run_script_action_ui } from 'src/types/RunScriptActionUI';
	import type { Device } from 'src/types/Device';
	import { devices, devices_settings } from 'src/stores';
	import { DO_WIZARD_STEPS_INDEXES } from '../../helpers/do-wizard-helpers';

	export let sceneData: any;

	const commandOptions = RUN_SCRIPT_SCENE_SET_VALUES.map((value) => ({
		value,
		title: value.charAt(0).toUpperCase() + value.slice(1),
	}));

	let scriptOptions: ScriptSelectOption[] = [];
	let isLoadingScripts = false;
	let loadError = '';
	let scriptsLoadSeq = 0;
	let scriptsLoadedForDeviceKey = '';

	const selectValue = (detail: unknown): string => {
		if (typeof detail === 'string' || typeof detail === 'number') {
			return String(detail);
		}
		if (detail && typeof detail === 'object' && 'value' in detail) {
			return String((detail as { value: unknown }).value ?? '');
		}
		return '';
	};

	$: deviceKey = sceneData.selectedDevice?.value || '';
	$: selectedScriptId =
		sceneData.scriptAction?.scriptId != null ? String(sceneData.scriptAction.scriptId) : '';
	$: selectedCommand = (
		RUN_SCRIPT_SCENE_SET_VALUES as readonly string[]
	).includes(String(sceneData.scriptAction?.command))
		? (sceneData.scriptAction.command as RunScriptCommand)
		: 'start';
	$: selectedScriptOption = scriptOptions.find((o) => o.value === selectedScriptId) ?? null;
	$: selectedCommandOption =
		commandOptions.find((o) => o.value === selectedCommand) ?? commandOptions[0];

	const validateCommandStep = () => {
		const scriptId = Number(sceneData.scriptAction?.scriptId);
		if (!deviceKey || !Number.isFinite(scriptId)) {
			sceneData.isValid = false;
			sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.RUN_SCRIPT_COMMAND_STEP;
			return;
		}
		sceneData.isValid = true;
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.RUN_SCRIPT_COMMAND_STEP;
	};

	if (is_run_script_action_ui(sceneData.translated)) {
		validateCommandStep();
	}

	const applyScriptOptions = (
		options: ScriptSelectOption[],
		noScriptsMessage: string,
	) => {
		scriptOptions = options;
		loadError = options.length === 0 ? noScriptsMessage : '';
	};

	const tryApplyWizardScriptList = (currentDeviceKey: string): boolean => {
		const scriptAction = sceneData.scriptAction;
		const device = $devices[currentDeviceKey] as Device | undefined;
		if (!device || !scriptAction) {
			return false;
		}

		const deviceRealId = getDeviceRealId(device);
		if (
			scriptAction.scriptListDeviceKey === currentDeviceKey &&
			scriptAction.scriptListRealId === deviceRealId &&
			Array.isArray(scriptAction.scriptListOptions) &&
			scriptAction.scriptListOptions.length > 0
		) {
			applyScriptOptions(scriptAction.scriptListOptions, '');
			return true;
		}

		return false;
	};

	const tryApplySessionOrSettingsCache = (currentDeviceKey: string): boolean => {
		const device = $devices[currentDeviceKey] as Device | undefined;
		if (!device) {
			applyScriptOptions([], 'Device not found');
			return false;
		}

		const deviceRealId = getDeviceRealId(device);
		const settings = ($devices_settings[deviceRealId] || {}) as Record<string, unknown>;
		const sessionScripts = getCachedDeviceScripts(deviceRealId);

		if (sessionScripts) {
			applyScriptOptions(
				mapScriptsToSelectOptions(sessionScripts, settings),
				$format('no_scripts_on_device', { default: 'No scripts on this device' }),
			);
			return sessionScripts.length > 0;
		}

		return false;
	};

	const loadScriptsForDevice = async (currentDeviceKey: string) => {
		const noScriptsMessage = $format('no_scripts_on_device', {
			default: 'No scripts on this device',
		});

		if (!currentDeviceKey) {
			scriptsLoadSeq += 1;
			scriptsLoadedForDeviceKey = '';
			scriptOptions = [];
			loadError = '';
			isLoadingScripts = false;
			return;
		}

		if (currentDeviceKey === scriptsLoadedForDeviceKey) {
			return;
		}

		scriptsLoadedForDeviceKey = currentDeviceKey;
		const loadSeq = ++scriptsLoadSeq;

		if (tryApplyWizardScriptList(currentDeviceKey)) {
			isLoadingScripts = false;
			return;
		}

		if (tryApplySessionOrSettingsCache(currentDeviceKey)) {
			isLoadingScripts = false;
			return;
		}

		const device = $devices[currentDeviceKey] as Device | undefined;
		if (!device) {
			applyScriptOptions([], 'Device not found');
			isLoadingScripts = false;
			return;
		}

		isLoadingScripts = true;
		const scripts = await loadDeviceScripts(device, $devices_settings);

		if (loadSeq !== scriptsLoadSeq || currentDeviceKey !== scriptsLoadedForDeviceKey) {
			return;
		}

		const deviceRealId = getDeviceRealId(device);
		const settings = ($devices_settings[deviceRealId] || {}) as Record<string, unknown>;
		const options = mapScriptsToSelectOptions(scripts, settings);

		sceneData.scriptAction = {
			...sceneData.scriptAction,
			scriptListDeviceKey: currentDeviceKey,
			scriptListRealId: deviceRealId,
			scriptListOptions: options,
		};

		applyScriptOptions(options, noScriptsMessage);
		isLoadingScripts = false;
	};

	$: loadScriptsForDevice(deviceKey);

	const handleScriptChange = (detail: unknown) => {
		const scriptId = selectValue(detail);
		const selectedScript = scriptOptions.find((option) => option.value === scriptId);
		sceneData.scriptAction = {
			...sceneData.scriptAction,
			scriptId: Number(scriptId),
			scriptName: selectedScript?.title,
		};
		validateCommandStep();
	};

	const handleCommandChange = (detail: unknown) => {
		const command = selectValue(detail);
		if (!RUN_SCRIPT_SCENE_SET_VALUES.includes(command as RunScriptCommand)) {
			return;
		}
		sceneData.scriptAction = {
			...sceneData.scriptAction,
			command,
		};
		validateCommandStep();
	};
</script>

<Stack gap="m">
	<Text element="h2" class="mb-0">
		{$format('wizard_step_run_script', { default: 'Script and command' })}
	</Text>

	{#if isLoadingScripts}
		<Text variant="body" size="s">{$format('loading', { default: 'Loading...' })}</Text>
	{/if}

	{#if loadError}
		<Alert type="warning" heading={loadError} />
	{/if}

	<Select
		id="scene-wizard-run-script-id"
		label={$format('label_script', { default: 'Script' })}
		options={scriptOptions}
		value={selectedScriptOption}
		disabled={scriptOptions.length === 0 || isLoadingScripts}
		on:change={(e) => handleScriptChange(e.detail)}
	/>

	<Select
		id="scene-wizard-run-script-command"
		label={$format('label_command', { default: 'Command' })}
		options={commandOptions}
		value={selectedCommandOption}
		on:change={(e) => handleCommandChange(e.detail)}
	/>
</Stack>
